import React, { memo, useEffect, useState } from "react";
import { useTheme } from "styled-components/native";
import styled from "styled-components/native";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { VDBIcons } from "@utils/VDBIcons";
import Banner from "./index";
import { SpaceType } from "../Spacer/types";
import { useGlobalData } from "@redux/slices/globalSlice";
import { VDBText } from "..";
import { useTranslation } from "react-i18next";
import Routes from "@navigations/Routes";
import { navigate } from "@navigations/utils/RootNavigation";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { Platform } from "react-native";
import { getDaysLeft } from "@scenes/more/MoreScreen/utils";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { useGetUser } from "@apis/user";
import { useIsFoldable } from "@navigations/utils/FoldableWrapper";
import { useUserAnalytics } from "../../../hooks/useAnalytics";
import { EventName, EventType, ViewType, ViewTypeEnum } from "@utils/analytics";
import { DefaultTheme } from "styled-components/native";

const FreeTrialBanner = () => {
  const theme = useTheme();
  const globalData = useGlobalData();
  const { t } = useTranslation();
  const typography = useTypographyStore();
  const { deviceWidth: width } = useIsFoldable();
  const userInfo = useUserInfo();
  const isWeb = Platform.OS === "web";
  const isAdmin = userInfo.isAdmin();
  const currentPlan = userInfo.user.plan_details?.plan;
  const planExpiry = currentPlan?.expiry;
  const daysLeftResult = getDaysLeft(planExpiry);
  const { daysLeft, daysLeftNoDate } =
    typeof daysLeftResult === "string"
      ? { daysLeft: 0, daysLeftNoDate: "" }
      : daysLeftResult;
  const planExpired =
    daysLeft <= 0 || Object.keys(userInfo.user.plan_details).length === 0;
  const textConfig = globalData?.subscription_config?.free_trial_banner;
  const bannerType =
    daysLeft <=
      globalData?.subscription_config?.free_trial_banner?.error?.show_in_days ||
    planExpired
      ? "error"
      : "warning";
  const iconColor =
    bannerType === "error" ? theme["core-01"] : theme["warn-01"];
  const bodyColor =
    bannerType === "error" ? theme["core-01"] : theme["core-05"];
  const [userId, setUserId] = useState(userInfo.user.id);
  const {} = useGetUser(userId, setUserId);
  const { trackButtonEvent } = useUserAnalytics();
  const handleLinkPress = () => {
    trackButtonEvent(
      EventName.HYPER_LINK_CLICK,
      EventType.BANNER_LINK_CLICK,
      { component_type: ViewType.BANNER },
      ViewTypeEnum?.PAID_MARKETPLACE_BANNER
    );
    setUserId(userInfo.user.id);
    if (theme.isLargeScreen) {
      navigate(Routes.ExploreNavigator, {
        screen: Routes.AccountOverviewScreen,
      });
    } else {
      navigate(Routes.MoreNavigator, {
        screen: Routes.AccountOverviewUsersSeats,
        initial: false,
      });
    }
  };

  useEffect(() => {
    trackButtonEvent(
      EventName.VIEW,
      null,
      { component_type: ViewType.BANNER },
      ViewTypeEnum?.PAID_MARKETPLACE_BANNER
    );
  }, []);

  const getText = () => {
    if (planExpired && isWeb) {
      if (isAdmin) {
        if (userInfo.isUpcomingNotPaidPlan()) {
          return t(
            "banner.freeTrial.expired.admin.upcoming.screenText.text",
            "Your Free Trial Has Ended - After payment, {{link}} to update your access."
          );
        }
        return t(
          "banner.freeTrial.expired.admin.screenText.text",
          "Your Free Trial Has Ended — Activate your Membership in the {{link}} to stay connected!"
        );
      } else {
        return t(
          "banner.freeTrial.expired.member.screenText.text",
          "Your Free Trial Has Ended — Please contact your company admin to activate a Membership Plan."
        );
      }
    }
    switch (bannerType) {
      case "warning":
        if (userInfo.isEnrolledTrial()) {
          if (userInfo.isAdmin()) {
            if (theme.isLargeScreen || isWeb) {
              return t(
                "banner.freeTrial.enrolledTrial.warning.admin.web.text",
                "ACTION REQUIRED: {{days}} left. {{link}} to review and update your users."
              );
            } else {
              return t(
                "banner.freeTrial.enrolledTrial.warning.admin.mobile.text",
                "ACTION REQUIRED: {{days}} left. Go to your {{link}} to review and update your users."
              );
            }
          } else {
            return t(
              "banner.freeTrial.enrolledTrial.warning.member.text",
              "ACTION REQUIRED: {{days}} left. Please contact your company admin in case of any issues."
            );
          }
        }
        return !isWeb
          ? !isAdmin
            ? t(
                "banner.freeTrial.mobile.warning.member.screenText.text",
                "{{days}} Free Trial! Please contact your company admin to purchase a membership plan."
              )
            : isAdmin && userInfo.isUpcomingNotPaidPlan()
            ? t(
                "banner.freeTrial.mobile.warning.upcoming.member.screenText.text",
                "{{days}} Free Trial! After payment, {{link}} to update your access."
              )
            : t(
                `${textConfig?.warning?.mobile?.text}`,
                "{{days}} Free Trial! Go to your {{link}} to see your account details."
              )
          : !isAdmin
          ? t(
              "banner.freeTrial.web.warning.member.screenText.text",
              "{{days}} Free Trial! Please contact your company admin to purchase a membership plan."
            )
          : isAdmin && userInfo.isUpcomingNotPaidPlan()
          ? t(
              "banner.freeTrial.web.warning.upcoming.member.screenText.text",
              "{{days}} Free Trial! After payment, {{link}} to update your access."
            )
          : t(
              `${textConfig?.warning?.web?.text}`,
              "{{days}} Free Trial!  Please {{link}} to set up a membership plan."
            );
      case "error":
        if (userInfo.isEnrolledTrial()) {
          if (userInfo.isAdmin()) {
            if (theme.isLargeScreen || isWeb) {
              return t(
                "banner.freeTrial.enrolledTrial.error.admin.web.text",
                "FINAL DAYS LEFT: {{days}} remaining. {{link}} to review and update your users."
              );
            } else {
              return t(
                "banner.freeTrial.enrolledTrial.error.admin.mobile.text",
                "FINAL DAYS LEFT: {{days}} remaining. Go to your {{link}} to review and update your users."
              );
            }
          } else {
            return t(
              "banner.freeTrial.enrolledTrial.error.member.text",
              "FINAL DAYS LEFT: {{days}} remaining. Please contact your company admin in case of any issues."
            );
          }
        }
        return !isWeb
          ? !isAdmin
            ? t(
                "banner.freeTrial.mobile.error.member.screenText.text",
                "{{days}} Free Trial! Please contact your company admin to purchase a membership plan."
              )
            : isAdmin && userInfo.isUpcomingNotPaidPlan()
            ? t(
                "banner.freeTrial.web.warning.upcoming.member.screenText.text",
                "{{days}} Free Trial! After payment, {{link}} to update your access."
              )
            : t(
                `${textConfig?.error?.mobile?.text}`,
                "{{days}} Free Trial! Go to your {{link}} to see your account details."
              )
          : !isAdmin
          ? t(
              "banner.freeTrial.web.error.member.screenText.text",
              "{{days}} Free Trial! Please contact your company admin to purchase a membership plan."
            )
          : isAdmin && userInfo.isUpcomingNotPaidPlan()
          ? t(
              "banner.freeTrial.web.warning.upcoming.member.screenText.text",
              "{{days}} Free Trial! After payment, {{link}} to update your access."
            )
          : t(
              `${textConfig?.error?.web?.text}`,
              "{{days}} Free Trial!  Please {{link}} to set up a membership plan."
            );
    }
  };
  const text = getText();
  const message = t(text, { days: daysLeftNoDate });
  const TextContent = () => {
    const messageParts = message.includes("{{link}}")
      ? message.split("{{link}}")
      : [message, ""];
    const firstPart = planExpired
      ? messageParts[0].replace(
          `${daysLeftNoDate} Free Trial!`,
          "Free Trial Ended!"
        )
      : !isWeb && messageParts[0]
      ? messageParts[0].replace(" Free Trial!", " Free Trial!\n")
      : messageParts[0];
    const trialMatch = messageParts[0].match(
      /-?\d+\s*Day[s]?\s*Free\s*Trial!?/i
    );
    const trialText = trialMatch ? trialMatch[0] : messageParts[0];
    const remainingText =
      messageParts[0].replace(trialText, "").trim() + messageParts[1];
    const remainingTextParts = remainingText.replace(
      userInfo.isUpcomingNotPaidPlan() ? "After payment," : "Go to your",
      userInfo.isUpcomingNotPaidPlan()
        ? "After payment, {{link}}"
        : "Go to your {{link}}"
    );
    const remainingTextPartsMobile = remainingText.replace(
      "Please",
      "Please {{link}}"
    );
    const firstPartText = planExpired
      ? "Your Free Trial Has Ended!"
      : trialText;
    const MobileContent = () => {
      if (userInfo.isEnrolledTrial() && isAdmin) {
        const enrolledFirstPart = messageParts[0].split("Go to your")[0].trim();
        return (
          <MobileContainer>
            <FlexContainer>
              <VDBIcon
                variant={IconsVariant.VDBCustom}
                name={VDBIcons.WARNING}
                color={iconColor}
                size={24}
              />
              <VDBText color={bodyColor} type={typography["Body-M"]}>
                {enrolledFirstPart}
              </VDBText>
            </FlexContainer>
            <VDBText
              color={bodyColor}
              type={typography["Body-M"]}
              textAlign="center"
            >
              {"Go to your "}
              <StyledLinkText bannerType={bannerType} onPress={handleLinkPress}>
                {t("banner.freeTrial.profileSection.mobile.text", "profile")}
              </StyledLinkText>
              {messageParts[1]}
            </VDBText>
          </MobileContainer>
        );
      }
      return (
        <MobileContainer>
          <FlexContainer>
            <VDBIcon
              variant={IconsVariant.VDBCustom}
              name={VDBIcons.WARNING}
              color={iconColor}
              size={24}
            />
            <VDBText color={bodyColor} type={typography["Body-M"]}>
              {firstPartText}
            </VDBText>
          </FlexContainer>
          {userInfo.isEnrolledTrial() ? null : (
            <VDBText
              color={bodyColor}
              type={typography["Body-M"]}
              textAlign="center"
            >
              {planExpired &&
                !isAdmin &&
                messageParts[0].split(" Free Trial!")[1] + " "}
              {remainingTextParts.split("{{link}}")[0]}
              {isAdmin && (
                <StyledLinkText
                  bannerType={bannerType}
                  onPress={handleLinkPress}
                >
                  {userInfo.isUpcomingNotPaidPlan()
                    ? t(
                        "banner.freeTrial.profileSection.mobile.upcoming.text",
                        "click here"
                      )
                    : t(
                        "banner.freeTrial.profileSection.mobile.text",
                        "profile"
                      )}
                </StyledLinkText>
              )}
              {userInfo.isUpcomingNotPaidPlan() ? "" : " "}
              {remainingTextParts.split("{{link}}")[1]}
            </VDBText>
          )}
        </MobileContainer>
      );
    };

    const WebContent = () => {
      return (
        <VDBText
          color={bodyColor}
          type={typography["Body-M"]}
          textAlign="center"
        >
          <FlexContainer>
            {!theme.isLargeScreen && (
              <VDBIcon
                variant={IconsVariant.VDBCustom}
                name={VDBIcons.WARNING}
                color={iconColor}
                size={24}
              />
            )}
            {theme.isLargeScreen ? firstPart : `${firstPartText}\n`}
          </FlexContainer>
          {!theme.isLargeScreen && `${"\n"}`}
          {!userInfo.isEnrolledTrial() &&
            !theme.isLargeScreen &&
            (userInfo.isUpcomingNotPaidPlan() && !planExpired
              ? messageParts[0].split("! ")[1]
              : userInfo.isUpcomingNotPaidPlan() && planExpired
              ? messageParts[0].split("Ended -")[1]
              : planExpired
              ? messageParts[0].split("— ")[1]
              : remainingTextPartsMobile.split("{{link}}")[0])}
          <StyledLinkText bannerType={bannerType} onPress={handleLinkPress}>
            {userInfo.isEnrolledTrial && !theme.isLargeScreen
              ? t(
                  "banner.freeTrial.profileSection.web.enrolled.text",
                  "profile"
                )
              : userInfo.isUpcomingNotPaidPlan()
              ? t(
                  "banner.freeTrial.profileSection.web.upcoming.text",
                  "click here"
                )
              : planExpired
              ? t(
                  "banner.freeTrial.profileSection.web.expired.text",
                  "Profile section"
                )
              : t(
                  "banner.freeTrial.profileSection.web.text",
                  "Visit the profile section"
                )}
          </StyledLinkText>
          {messageParts[1]}
        </VDBText>
      );
    };
    if (!isWeb) {
      return <MobileContent />;
    }
    return message && message.includes("{{link}}") ? (
      <WebContent />
    ) : (
      <VDBText color={bodyColor} type={typography["Body-M"]}>
        {Platform.OS !== "web"
          ? message?.replace(" Free Trial", " Free Trial\n")
          : message}
      </VDBText>
    );
  };

  const BannerContent = () => {
    return (
      <Container width={width}>
        {(theme.isLargeScreen ||
          (!theme.isLargeScreen && !isAdmin && isWeb)) && (
          <VDBIcon
            variant={IconsVariant.VDBCustom}
            name={VDBIcons.WARNING}
            color={iconColor}
            size={24}
          />
        )}
        <TextContent />
      </Container>
    );
  };
  return (
    <Banner
      bgColor={bannerType === "error" ? theme["error-01"] : theme["warn-02"]}
      children={<BannerContent />}
    />
  );
};

const Container = styled.View`
  width: ${({ width, theme }: { width: number; theme: DefaultTheme }) =>
    theme.isMobile ? width - 60 : width}px;
  flex-direction: row;
  ${({ theme }: { theme: DefaultTheme }) =>
    !theme.isLargeScreen ? "" : "align-items: center;"}
  justify-content: center;
  gap: ${SpaceType.Basic};
  align-items: center;
  padding-vertical: ${SpaceType.Basic};
`;

const StyledLinkText = styled.Text<{ bannerType: string }>`
  color: ${({
    theme,
    bannerType,
  }: {
    theme: DefaultTheme;
    bannerType: string;
  }) => (bannerType === "error" ? theme["core-01"] : theme["link-static"])};
  text-decoration-line: ${({ bannerType }: { bannerType: string }) =>
    bannerType === "error" ? "underline" : "none"};
`;
const MobileContainer = styled.View``;
const FlexContainer = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

export default memo(FreeTrialBanner);
